import { useCustomAttributeFilters } from "#hooks/FilterPanel/useCustomAttributeFilters";
import { useDeleteNetwork, useListNetworks } from "#hooks/adapters/useNetworks";
import { useGetManyReportingGroups } from "#hooks/adapters/useReportingGroups";
import { useNetworkHeaders } from "#hooks/tables/useNetworkHeaders";
import {
  useTableSortingAndPagination,
  useTablesSortingAndPagination,
} from "#redux/reducers/tableStateReducer";
import EditAssetGroupDialog from "#src/batteries-included-components/Dialogs/EditAssetGroupDialog";
import { NetworksTableFilterArea } from "#src/batteries-included-components/FilterAreas/AssetsFilterAreas";
import { useStickyState } from "#src/hooks/useStickyState";
import {
  useDataTableColumnConfigurationProps,
  useDataTableGroupBy,
  useDataTableGroupByProps,
  useDataTableMetaExtendedHeaders,
  useDataTableMetaHeaders,
  useDataTableUserInterfaceConfiguration,
} from "#src/utils/table";
import { useQueries } from "@tanstack/react-query";
import {
  Button,
  DataTablePanel,
  Dialog,
  getDataTableGroupByKeysForGroupByData,
  ItemAction,
  StorageKeys,
} from "@validereinc/common-components";
import {
  AssetGroupAdapter,
  AssetGroupType,
  AssetType,
  SortDirection,
  type GroupedByEntryType,
} from "@validereinc/domain";
import {
  getYearMonthFromDateRange,
  toFlattenedObject,
} from "@validereinc/utilities";
import React, { useMemo, useState } from "react";

export const NetworkDataTablePanel = ({
  filterConfigStorageKey,
  viewConfigStorageKey,
  tableConfigStorageKey,
  tableStateStorageKey,
  groupByTablesStateStorageKey,
}: StorageKeys) => {
  const [assetIdToEdit, setAssetIdToEdit] = useState<string | undefined>();
  const [networkToDelete, setNetworkToDelete] = useState<AssetGroupType>();
  const [viewFilters] = useStickyState({}, viewConfigStorageKey);
  const [tableFilters] = useStickyState({}, filterConfigStorageKey);
  const {
    saveDefaultConfigurationMutation,
    isSaveConfigurationAvailable,
    resetConfigurationMutation,
    deleteDefaultConfigurationMutation,
    isDeleteConfigurationAvailable,
    isLoading: isLoadingTableUserInterfaceConfiguration,
    matchingDefaultConfiguration,
    matchingCompanyConfiguration,
    matchingDefaultTableUserInterfaceConfigurationsQuery,
  } = useDataTableUserInterfaceConfiguration(tableConfigStorageKey);
  const rawFilters = useMemo<any>(
    () => ({ ...tableFilters, ...viewFilters }),
    [tableFilters, viewFilters]
  );
  const { period, ...filters } = rawFilters;

  const [tableState, updateTableState] = useTableSortingAndPagination(
    {
      sortBy: "name",
      sortDirection: SortDirection.ASCENDING,
    },
    filters,
    tableStateStorageKey
  );

  const networkListQueryPayload: Parameters<typeof useListNetworks>[0] = {
    page: tableState.page,
    pageSize: tableState.pageSize,
    sortBy: tableState.sortBy,
    sortDirection: tableState.sortDirection,
    groupBy: tableState.groupBy,
    filters: toFlattenedObject(filters),
    meta: { period: getYearMonthFromDateRange(period) },
  };

  const {
    data: networkData,
    isLoading: isNetworkDataLoading,
    isFetching: isNetworkDataFetching,
  } = useListNetworks(networkListQueryPayload);
  const items = networkData?.data ?? [];

  const groupByKeys = getDataTableGroupByKeysForGroupByData(
    items as unknown as GroupedByEntryType[]
  );
  const [groupByTablesStates, updateGroupByTablesStates] =
    useTablesSortingAndPagination(
      groupByKeys,
      {
        ...networkListQueryPayload,
        page: 1,
        pageSize: 10,
        sortBy: "created_at",
        sortDirection: SortDirection.DESCENDING,
      },
      groupByTablesStateStorageKey
    );

  const {
    queryPayloads: groupByQueryPayloads,
    setActiveGroupByGroups,
    totalSelectableItems,
  } = useDataTableGroupBy(
    items as unknown as GroupedByEntryType[],
    tableState.groupBy,
    groupByKeys.map(
      (key) => groupByTablesStates.get(key) ?? networkListQueryPayload
    )
  );

  const networkGroupedByQueries = useQueries({
    queries: groupByQueryPayloads.map((d) => ({
      queryKey: ["networks", "getList", d.payload],
      queryFn: () => AssetGroupAdapter.getList(d.payload),
      enabled: d.enabled,
    })),
  });

  const metaHeaders = useDataTableMetaHeaders();
  const metaExtendedHeaders = useDataTableMetaExtendedHeaders(
    tableState.groupBy.length
      ? networkGroupedByQueries.flatMap((q) => q.data?.data ?? [])
      : items
  );

  const reportingGroupQuery = useGetManyReportingGroups(
    Array.from(
      new Set(
        tableState.groupBy.length
          ? networkGroupedByQueries.flatMap((q) =>
              q.data?.data.map((d) => d.default_network_reporting_group_id)
            )
          : items.map(
              ({ default_network_reporting_group_id }) =>
                default_network_reporting_group_id as any
            )
      )
    ).filter(
      (item) => item && (typeof item === "string" || item instanceof String)
    )
  );

  const reportingGroupMap = reportingGroupQuery.reduce(
    (accumulator: Record<string, string>, current) => {
      if ((current.data as any)?.id) {
        accumulator[(current.data as any).id] = (current.data as any).name;
      }
      return accumulator;
    },
    {}
  );

  const deleteNetwork = useDeleteNetwork();

  const { customAttributeHeaders, isLoading: isCustomAttrsLoading } =
    useCustomAttributeFilters({
      assetType: AssetType.ASSET_GROUP,
      sortable: true,
      groupable: true,
    });
  const { isLoading: isLoadingNetworkHeaders, headers: networkheaders } =
    useNetworkHeaders({ reportingGroupMap, groupable: true });

  const headers = useMemo(
    () => [
      ...networkheaders,
      ...customAttributeHeaders,
      ...metaHeaders,
      ...metaExtendedHeaders,
    ],
    [networkheaders, customAttributeHeaders, metaHeaders, metaExtendedHeaders]
  );

  const onClickDeleteNetwork = async () => {
    try {
      if (networkToDelete) {
        deleteNetwork.mutate(networkToDelete.id);
      }
    } finally {
      setNetworkToDelete(undefined);
    }
  };

  const getItemActions = ({ item }: { item: AssetGroupType }): ItemAction[] => [
    {
      label: "Edit",
      buttonProps: {
        onClick: () => setAssetIdToEdit(item.id),
        icon: "pencil-simple",
      },
    },
    {
      label: "Delete",
      buttonProps: {
        onClick: () => {
          setNetworkToDelete(item);
        },
        icon: "trash",
      },
    },
  ];

  const groupByDataTableProps = useDataTableGroupByProps({
    items,
    groupBy: tableState.groupBy,
    updateTableState,
    setActiveGroupByGroups,
    groupByQueries: networkGroupedByQueries,
    groupByTablesStates,
    updateGroupByTablesStates,
    headers,
    getItemId: (item) => item.id,
    totalSelectableItems,
    getItemActions,
  });
  const columnConfigurationDataTableProps =
    useDataTableColumnConfigurationProps({
      configurationName: "Default for Networks list table",
      saveDefaultConfigurationMutation,
      isSaveConfigurationAvailable,
      resetConfigurationMutation,
      deleteDefaultConfigurationMutation,
      isDeleteConfigurationAvailable,
      isLoading: isLoadingTableUserInterfaceConfiguration,
      matchingDefaultConfiguration,
      matchingCompanyConfiguration,
      matchingDefaultTableUserInterfaceConfigurationsQuery,
    });

  const isLoading =
    isNetworkDataLoading || isCustomAttrsLoading || isLoadingNetworkHeaders;

  return (
    <>
      <DataTablePanel
        storageKey={tableConfigStorageKey}
        filterComponent={
          <NetworksTableFilterArea
            filterConfigStorageKey={filterConfigStorageKey}
            hasCustomAttributeFilters
          />
        }
        columnConfigurationProps={columnConfigurationDataTableProps}
        dataTableProps={{
          intent: "simplicity-first",
          isLoading,
          isBusy: isNetworkDataFetching,
          items,
          getItemId: (item) => item.id,
          headers,
          getItemActions,
          onSortChange: updateTableState,
          onPaginationChange: updateTableState,
          sorting: {
            sortBy: tableState.sortBy,
            sortDirection: tableState.sortDirection,
          },
          pagination: {
            page: tableState.page,
            pageSize: tableState.pageSize,
            total: networkData?.total_entries,
          },
        }}
        {...groupByDataTableProps}
      />
      <Dialog
        isOpen={!!networkToDelete}
        onClose={() => setNetworkToDelete(undefined)}
        title={`Delete "${networkToDelete?.name}"`}
        actionRow={[
          <Button
            key="delete-dialog-action"
            onClick={onClickDeleteNetwork}
            variant="error"
          >
            Delete
          </Button>,
        ]}
      >
        <div>
          Are you sure you want to delete this network? This action cannot be
          undone.
        </div>
      </Dialog>
      <EditAssetGroupDialog
        assetGroupId={assetIdToEdit}
        onClose={() => setAssetIdToEdit(undefined)}
      />
    </>
  );
};
